import { createAction } from '@reduxjs/toolkit';
import type { Patch } from 'rfc6902';
import { ProjectType } from './types';

interface DescriptorPropertyEdit {
  type: 'property';
  /**
   * The path to the property that is edited.
   */
  path: (string | number)[];
  /**
   * The new value of the property.
   */
  value: unknown;
  /**
   * The timestamp of the edit.
   */
  timestamp: number;
}

interface DescriptorPatchEdit {
  type: 'patch';
  /**
   * The JSONPatch to apply.
   */
  patch: Patch;
  /**
   * Ignore any and all errors when applying this patch.
   * This only exists for Redux-AngularJS sync. Do not use it otherwise.
   * Will be removed after AngularJS is no longer used for state changes.
   * @deprecated
   */
  ignoreErrors?: boolean;
  /**
   * The timestamp of the edit.
   */
  timestamp: number;
}

export type DescriptorEdit = DescriptorPropertyEdit | DescriptorPatchEdit;

/**
 * Action for initializing the descriptor editor state with a successfully loaded project.
 * Can also be used to completely reset the editor state.
 */
export const initializeEditor = createAction<{
  id: string;
  descriptor: unknown;
  type: ProjectType;
  version: string;
  editable: boolean;
  navigationUrl?: string | null;
  mobile_service_config?: unknown;
}>('editor/initialize');

export const initializeEditorFailed = createAction<void>('editor/init/failed');

/**
 * Action dispatched when project saving starts.
 */
export const saveProjectPending = createAction<void>('editor/save/pending');

/**
 * Action dispatched when project saving has failed.
 */
export const saveProjectFailure = createAction<void>('editor/save/failure');

/**
 * Action dispatched when project save has successfully completed.
 */
export const saveProjectSuccess = createAction<{ version: string; descriptor: unknown }>('editor/save/success');

/**
 * Action for changing the current navigational state of the editor.
 */
export const navigateEditor = createAction<string | null>('editor/navigate');

/**
 * Action for editing the descriptor.
 * This change is pushed to the undo stack (or amended if applicable).
 */
export const editDescriptor = createAction<DescriptorEdit>('editor/edit');

/**
 * Action for undoing the latest change to the descriptor, if any.
 */
export const undoLatestChange = createAction('editor/undo');

/**
 * Action for redoing the latest undone change to the descriptor, if any.
 */
export const redoLatestUndo = createAction('editor/redo');

/**
 * Discards all the local changes done to the descriptor, making the state match the latest known remote state.
 * This also clears the undo & redo stacks.
 */
export const revertCurrentDescriptor = createAction<void>('editor/revert');

/**
 * Merge incoming upstream changes done to the remote descriptor since the editor was loaded.
 * In practice this will be similar to a "rebase" in GIT: the local changes are tried to be re-applied
 * on top of the remote changes.
 *
 * If there are conflicts, then the local state is unaltered, meaning that the attempt to save
 * would possibly overwrite remote changes.
 */
export const rebaseEditorProject = createAction<{ version: string; patch: Patch }>('editor/rebase');

/**
 * Action helper for an edit using the given patch.
 * NOTE: The ignoreErrors parameter only exists for Redux-AngularJS sync. Do not use it otherwise.
 * @param patch the JSONPatch to apply
 * @param ignoreErrors Deprecated: if true, ignores ALL errors when attempting to apply the patch
 */
export function editCurrentDescriptorWithPatch(patch: Patch, ignoreErrors = false) {
  return editDescriptor({
    type: 'patch',
    patch,
    ignoreErrors,
    timestamp: Date.now(),
  });
}

/**
 * Action helper for an edit of a single property by path.
 */
export function editCurrentDescriptorProperty(path: (string | number)[], value: unknown) {
  return editDescriptor({
    type: 'property',
    path,
    value,
    timestamp: Date.now(),
  });
}

/**
 * Reset the initial migration diff patch, e.g. when the project is saved.
 */
export const resetInitialMigrationDiff = createAction<void>('editor/resetInitialMigrationDiff');

/**
 * Updates the mobile service configuration in the editor state.
 * @param config The mobile service configuration or null to clear it
 */
export const setMobileServiceConfig = createAction<unknown | null>('editor/setMobileServiceConfig');
